import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Webhook, Key, Plus, Trash2, Loader2, ExternalLink, CheckCircle, XCircle, Copy, Check, Code } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";
import { logger } from "@/utils/logger";

const DeveloperPortal = ({ user }) => {
  const [events, setEvents] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ url: "", events: [], secret: "" });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [evRes, subRes] = await Promise.all([
        api.get("/webhooks/events"),
        user ? api.get("/webhooks/subscriptions", { withCredentials: true }) : Promise.resolve({ data: { subscriptions: [] } }),
      ]);
      setEvents(evRes.data.events || []);
      setSubscriptions(subRes.data.subscriptions || []);
    } catch (err) { logger.error("Fetch error:", err); } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.url || form.events.length === 0) {
      toast.error("URL and at least one event are required");
      return;
    }
    setCreating(true);
    try {
      await api.post("/webhooks/subscribe", form, { withCredentials: true });
      toast.success("Webhook subscription created");
      setShowCreate(false);
      setForm({ url: "", events: [], secret: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create subscription");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (subId) => {
    try {
      await api.delete(`/webhooks/subscriptions/${subId}`, { withCredentials: true });
      toast.success("Subscription deleted");
      setSubscriptions(subscriptions.filter(s => s.subscription_id !== subId));
    } catch (err) {
      logger.error("Action error:", err);
      toast.error("Failed to delete");
    }
  };

  const toggleEvent = (evt) => {
    setForm(prev => ({
      ...prev,
      events: prev.events.includes(evt) ? prev.events.filter(e => e !== evt) : [...prev.events, evt],
    }));
  };

  const copySecret = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12 px-6" data-testid="developer-portal">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
              <Code className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <h1 className="text-3xl text-[#EDEDED]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Developer Portal
            </h1>
          </div>
          <p className="text-[#A1A1AA] text-sm ml-[52px]">
            Integrate with Breedling via webhooks. Receive real-time events for auctions, listings, and subscriptions.
          </p>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
          <a
            href="/api/docs"
            target="_blank"
            className="glass-card p-4 flex items-center gap-3 hover:border-[#D4AF37]/30 transition-colors group"
            data-testid="link-swagger"
          >
            <ExternalLink className="w-4 h-4 text-[#D4AF37] group-hover:translate-x-0.5 transition-transform" />
            <div>
              <p className="text-sm text-[#EDEDED]">API Docs (Swagger)</p>
              <p className="text-xs text-[#71717A]">Interactive API explorer</p>
            </div>
          </a>
          <a
            href="/api/redoc"
            target="_blank"
            className="glass-card p-4 flex items-center gap-3 hover:border-[#D4AF37]/30 transition-colors group"
            data-testid="link-redoc"
          >
            <ExternalLink className="w-4 h-4 text-[#D4AF37] group-hover:translate-x-0.5 transition-transform" />
            <div>
              <p className="text-sm text-[#EDEDED]">ReDoc</p>
              <p className="text-xs text-[#71717A]">Read-only documentation</p>
            </div>
          </a>
          <a
            href="/api/openapi.json"
            target="_blank"
            className="glass-card p-4 flex items-center gap-3 hover:border-[#D4AF37]/30 transition-colors group"
            data-testid="link-openapi"
          >
            <ExternalLink className="w-4 h-4 text-[#D4AF37] group-hover:translate-x-0.5 transition-transform" />
            <div>
              <p className="text-sm text-[#EDEDED]">OpenAPI Spec</p>
              <p className="text-xs text-[#71717A]">JSON schema for code generation</p>
            </div>
          </a>
        </div>

        {/* Available Events */}
        <div className="glass-card p-6 mb-6" data-testid="webhook-events-list">
          <h2 className="text-lg text-[#EDEDED] mb-4 flex items-center gap-2">
            <Webhook className="w-5 h-5 text-[#D4AF37]" /> Webhook Events
          </h2>
          <p className="text-xs text-[#71717A] mb-4">
            Subscribe to these events to receive HTTP POST callbacks when actions occur on the platform.
            Payloads are signed with HMAC-SHA256 using your webhook secret.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {events.map((evt) => (
              <div key={evt} className="flex items-center gap-2 p-3 bg-[#0A0A0A] rounded-lg">
                <div className="w-2 h-2 rounded-full bg-[#D4AF37]" />
                <code className="text-sm text-[#EDEDED] font-mono">{evt}</code>
              </div>
            ))}
          </div>
        </div>

        {/* Subscriptions */}
        <div className="glass-card p-6 mb-6" data-testid="webhook-subscriptions">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg text-[#EDEDED] flex items-center gap-2">
              <Key className="w-5 h-5 text-[#D4AF37]" /> Your Subscriptions
            </h2>
            {user && (
              <button
                onClick={() => setShowCreate(true)}
                className="btn-gold text-sm px-3 py-1.5 flex items-center gap-1"
                data-testid="create-webhook-btn"
              >
                <Plus className="w-3 h-3" /> New Webhook
              </button>
            )}
          </div>

          {!user ? (
            <div className="text-center py-8">
              <p className="text-[#71717A] text-sm">Sign in to manage webhook subscriptions.</p>
              <Link to="/" className="text-[#D4AF37] text-sm hover:underline mt-2 inline-block">Sign In</Link>
            </div>
          ) : subscriptions.length === 0 ? (
            <div className="text-center py-8">
              <Webhook className="w-8 h-8 text-[#3F3F46] mx-auto mb-3" />
              <p className="text-[#71717A] text-sm">No webhook subscriptions yet.</p>
              <button onClick={() => setShowCreate(true)} className="text-[#D4AF37] text-sm hover:underline mt-2">
                Create your first webhook
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {subscriptions.map((sub) => (
                <div key={sub.subscription_id} className="flex items-center gap-3 p-4 bg-[#0A0A0A] rounded-lg">
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${sub.active ? "bg-[#22C55E]" : "bg-[#EF4444]"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#EDEDED] font-mono truncate">{sub.url}</p>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {sub.events?.map(evt => (
                        <span key={evt} className="text-xs px-1.5 py-0.5 bg-[#27272A] text-[#A1A1AA] rounded font-mono">
                          {evt}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(sub.subscription_id)}
                    className="p-2 text-[#71717A] hover:text-red-400 transition-colors flex-shrink-0"
                    data-testid={`delete-webhook-${sub.subscription_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Verification Example */}
        <div className="glass-card p-6" data-testid="verification-example">
          <h2 className="text-lg text-[#EDEDED] mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-[#22C55E]" /> Signature Verification
          </h2>
          <p className="text-xs text-[#71717A] mb-4">
            Each webhook delivery includes an <code className="text-[#D4AF37]">X-Breedling-Signature</code> header.
            Verify it using HMAC-SHA256 with your webhook secret:
          </p>
          <div className="relative">
            <pre className="bg-[#0A0A0A] p-4 rounded-lg text-xs text-[#A1A1AA] overflow-x-auto font-mono leading-relaxed">
{`import hmac, hashlib

def verify_signature(body: str, secret: str, signature: str) -> bool:
    expected = hmac.new(
        secret.encode(),
        body.encode(),
        hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(expected, signature)`}
            </pre>
            <button
              onClick={() => copySecret(`import hmac, hashlib\n\ndef verify_signature(body: str, secret: str, signature: str) -> bool:\n    expected = hmac.new(secret.encode(), body.encode(), hashlib.sha256).hexdigest()\n    return hmac.compare_digest(expected, signature)`)}
              className="absolute top-3 right-3 p-1.5 bg-[#18181B] rounded text-[#71717A] hover:text-[#D4AF37] transition-colors"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setShowCreate(false)}>
            <div className="glass-card p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="create-webhook-modal">
              <h3 className="text-xl text-[#EDEDED] mb-4">New Webhook Subscription</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-1.5">Endpoint URL *</label>
                  <input
                    type="url"
                    value={form.url}
                    onChange={(e) => setForm({ ...form, url: e.target.value })}
                    className="input-minimal w-full text-sm"
                    placeholder="https://your-server.com/webhooks/breedling"
                    data-testid="webhook-url-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-1.5">Signing Secret (optional)</label>
                  <input
                    type="text"
                    value={form.secret}
                    onChange={(e) => setForm({ ...form, secret: e.target.value })}
                    className="input-minimal w-full text-sm"
                    placeholder="whsec_..."
                    data-testid="webhook-secret-input"
                  />
                  <p className="text-xs text-[#52525B] mt-1">Used to verify webhook signatures via HMAC-SHA256</p>
                </div>
                
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">Events *</label>
                  <div className="grid grid-cols-2 gap-2">
                    {events.map(evt => (
                      <label key={evt} className="flex items-center gap-2 p-2 bg-[#0A0A0A] rounded cursor-pointer hover:bg-[#18181B]">
                        <input
                          type="checkbox"
                          checked={form.events.includes(evt)}
                          onChange={() => toggleEvent(evt)}
                          className="accent-[#D4AF37]"
                          data-testid={`event-check-${evt}`}
                        />
                        <span className="text-xs text-[#EDEDED] font-mono">{evt}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCreate(false)}
                  className="flex-1 py-2.5 text-sm rounded-lg border border-[#27272A] text-[#A1A1AA] hover:bg-[#18181B]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={creating}
                  className="flex-1 btn-gold text-sm py-2.5 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="submit-webhook-btn"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Create Webhook
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeveloperPortal;
